import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import axios from 'axios';

const STORAGE_KEY = 'aura.lang';
const SUPPORTED_LANGUAGES = ['en', 'zh-CN'];

// Region default: a build (e.g. the China deployment) can set REACT_APP_DEFAULT_LANG=zh-CN
// so the UI defaults to that language when the user has made no explicit choice and the
// browser is not already Chinese. Unset/unsupported values fall back to English.
const configuredDefault = process.env.REACT_APP_DEFAULT_LANG;
const DEFAULT_LANGUAGE =
  configuredDefault && SUPPORTED_LANGUAGES.includes(configuredDefault) ? configuredDefault : 'en';

/**
 * Initial language resolution:
 *   1. an explicit choice previously persisted in localStorage, else
 *   2. the browser language when it is Chinese, else
 *   3. English (the fallback).
 */
function detectInitialLanguage(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.includes(stored)) {
      return stored;
    }
  } catch {
    /* localStorage unavailable — fall through to browser detection */
  }

  const browser = typeof navigator !== 'undefined' ? navigator.language : 'en';
  return browser && browser.toLowerCase().startsWith('zh') ? 'zh-CN' : DEFAULT_LANGUAGE;
}

function applyDocumentLanguage(language: string): void {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = language;
  }
}

const initialLanguage = detectInitialLanguage();

i18n.use(initReactI18next).init({
  fallbackLng: 'en',
  lng: initialLanguage,
  supportedLngs: SUPPORTED_LANGUAGES,
  resources: {
    en: {
      translations: require('./locales/en/translations.json')
    },
    'zh-CN': {
      translations: require('./locales/zh-CN/translations.json')
    }
  },
  ns: ['translations'],
  defaultNS: 'translations'
});

i18n.languages = SUPPORTED_LANGUAGES;

// Forward the active UI language on EVERY API request so the backend can localize
// user-facing responses (validation and error messages). This is a single global
// interceptor on the shared axios instance, so it applies to all current and future
// API calls with no per-request change. It is language-agnostic: whatever language the
// app is set to is sent as-is via the standard `Accept-Language` header, and the backend
// resolves the culture (it accepts `?culture` / `X-Culture` / `Accept-Language`).
axios.interceptors.request.use((config) => {
  config.headers.set('Accept-Language', i18n.language || DEFAULT_LANGUAGE);
  return config;
});

applyDocumentLanguage(initialLanguage);

i18n.on('languageChanged', (language: string) => {
  applyDocumentLanguage(language);
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    /* ignore persistence errors (e.g. private browsing) */
  }
});

export default i18n;
